/**
 * PWA 설치 이벤트(beforeinstallprompt / appinstalled) 조기 포착 및 실제 설치 상태 관리 모듈
 * React 마운트 이전에 발생하는 브라우저 신호를 놓치지 않고 보관하며,
 * getInstalledRelatedApps API를 활용하여 실제 설치 여부를 신뢰성 있게 판정합니다.
 */

const STORAGE_KEY = 'moowoo_pwa_installed';
const MODAL_SHOWN_SESSION_KEY = 'moowoo_pwa_install_modal_shown';

let deferredPrompt = null;
let installedTicket = false;
let actuallyInstalled = null; // null: 판정 불가 / true: 설치됨 / false: 미설치
let lastInstalledAt = 0;
const subscribers = new Set();

let installPollTimer = null;
let installPollCount = 0;
let installPollBusy = false;          // 조회 지연 시 중복 실행 방지 빗장
const INSTALL_POLL_INTERVAL = 1000;   // 1초 간격 조회
const INSTALL_POLL_MAX = 20;          // 최대 20회(약 20초) 후 자동 종료

function notifySubscribers(eventData) {
  subscribers.forEach((callback) => {
    try {
      callback(eventData);
    } catch {
      // 구독자 콜백 예외 시 무시
    }
  });
}

/**
 * 브라우저의 getInstalledRelatedApps API를 통해 실제 PWA 설치 여부를 직접 조회
 * @returns {Promise<boolean|null>} true: 설치됨, false: 미설치, null: 판정 불가
 */
export async function queryActuallyInstalled() {
  try {
    if (typeof navigator === 'undefined' || typeof navigator.getInstalledRelatedApps !== 'function') {
      return null;
    }
    const relatedApps = await navigator.getInstalledRelatedApps();
    return Array.isArray(relatedApps) && relatedApps.length > 0;
  } catch {
    return null;
  }
}

/**
 * 실제 설치 상태를 재조회하고 결과를 구독자들에게 통보
 */
export async function refreshInstalledState() {
  const result = await queryActuallyInstalled();
  if (result !== null) {
    actuallyInstalled = result;
    notifySubscribers({ type: 'installed_checked', actuallyInstalled });
  }
}

/**
 * 현재 파악된 실제 설치 여부 반환 (null | boolean)
 */
export function getActuallyInstalled() {
  return actuallyInstalled;
}

/**
 * 설치 확정 처리 내부 공용 함수 (10초 쿨다운 및 단일 티켓 발급)
 */
function markInstalled() {
  const now = Date.now();
  if (now - lastInstalledAt < 10000) {
    return false;
  }
  lastInstalledAt = now;

  deferredPrompt = null;

  try {
    sessionStorage.removeItem(MODAL_SHOWN_SESSION_KEY);
  } catch {
    // 세션 스토리지 예외 방어
  }

  installedTicket = true;
  actuallyInstalled = true;

  notifySubscribers({ type: 'appinstalled' });
  return true;
}

/**
 * 설치 확인 폴링 중단 함수
 */
export function stopInstalledPolling() {
  if (installPollTimer) {
    clearInterval(installPollTimer);
    installPollTimer = null;
  }
  installPollCount = 0;
  installPollBusy = false;
}

/**
 * 설치 확인 폴링 시작 함수 (최대 20초간 1초 간격 조회)
 */
export function startInstalledPolling() {
  if (typeof window === 'undefined') return;
  if (installPollTimer) return; // 중복 실행 방지

  installPollCount = 0;
  installPollBusy = false;

  installPollTimer = setInterval(async () => {
    // 이전 조회가 아직 안 끝났으면 이번 회차는 건너뛴다
    if (installPollBusy) return;

    installPollCount += 1;

    if (installPollCount > INSTALL_POLL_MAX) {
      stopInstalledPolling();
      return;
    }

    // 이미 다른 경로(appinstalled)로 티켓이 발급됐으면 종료
    if (installedTicket) {
      stopInstalledPolling();
      return;
    }

    installPollBusy = true;
    try {
      const result = await queryActuallyInstalled();

      if (result === true) {
        stopInstalledPolling();
        markInstalled();
      } else if (result === null) {
        // 미지원 브라우저는 계속 물어봐야 소용없으므로 즉시 종료
        stopInstalledPolling();
      }
    } catch {
      stopInstalledPolling();
    } finally {
      installPollBusy = false;
    }
  }, INSTALL_POLL_INTERVAL);
}

/**
 * 보관된 beforeinstallprompt 이벤트 반환 (없으면 null)
 */
export function getDeferredPrompt() {
  return deferredPrompt;
}

/**
 * 보관된 beforeinstallprompt 이벤트 비우기
 */
export function clearDeferredPrompt() {
  deferredPrompt = null;
  notifySubscribers({ type: 'cleared' });
}

/**
 * 설치 이벤트 상태 변화 구독 (해제 함수 반환)
 * @param {(eventData?: any) => void} callback 
 * @returns {() => void} 구독 해제 함수
 */
export function subscribe(callback) {
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
  };
}

/**
 * 현재 설치 완료 티켓 보유 여부 반환 (appinstalled 1회 한정)
 * @returns {boolean}
 */
export function hasInstalledTicket() {
  return installedTicket;
}

/**
 * 설치 완료 티켓 소비 및 세션 도장 기록 (재노출 영구 방지)
 */
export function consumeInstalledTicket() {
  installedTicket = false;
  try {
    sessionStorage.setItem(MODAL_SHOWN_SESSION_KEY, '1');
  } catch {
    // 프라이빗 모드 등 세션스토리지 예외 방어
  }
  notifySubscribers({ type: 'ticket_consumed' });
}

/**
 * 현재 세션에서 설치 완료 모달이 이미 소비되었는지 확인
 * @returns {boolean}
 */
export function isInstallModalConsumedThisSession() {
  try {
    return sessionStorage.getItem(MODAL_SHOWN_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

// 모듈 초기화 즉시 이전 낡은 도장 청소 및 이벤트 리스너 등록
if (typeof window !== 'undefined') {
  // 이전 버전의 낡은 도장 오염 청소 (1회)
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // 스토리지 접근 예외 방어
  }

  // 실제 설치 여부 1회 비동기 초기 조회
  queryActuallyInstalled().then((result) => {
    if (result !== null) {
      actuallyInstalled = result;
      notifySubscribers({ type: 'installed_checked', actuallyInstalled });
    }
  });

  window.addEventListener('beforeinstallprompt', (e) => {
    try {
      e.preventDefault();
      deferredPrompt = e;

      // beforeinstallprompt 수신은 브라우저가 앱 미설치 상태임을 확정한 것이므로 미설치로 갱신
      actuallyInstalled = false;

      try {
        sessionStorage.removeItem(MODAL_SHOWN_SESSION_KEY);
      } catch {
        // 스토리지 접근 예외 방어
      }

      notifySubscribers({ type: 'beforeinstallprompt', prompt: e });
    } catch {
      // 예외 발생 시 무시
    }
  });

  window.addEventListener('appinstalled', () => {
    try {
      stopInstalledPolling();
      markInstalled();
    } catch {
      // 예외 발생 시 무시
    }
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      refreshInstalledState().catch(() => {});
    }
  });
}
